/**
 * MobXPersistence — referans-parite (features/platform/utils/MobXPersistence.ts).
 *
 * kaynak-referans bunu `mobx-persist-store` üzerine kurar; TEBHOS'ta ekstra bağımlılık
 * eklemek yerine aynı dış API'yi mobx `reaction` + mevcut AppStorage ile
 * el yapımı sağlıyoruz:
 *
 * - `makePersistent(store, key, properties, options)` — construct sırasında
 *   AppStorage'dan hidrasyon + 500ms debounce ile otomatik yazma.
 * - `options.syncAcrossTabs` — AppStorage `storage` olayı (source: "external")
 *   üzerinden sekmeler-arası yeniden hidrasyon.
 * - `stopPersistent(key, store)` — reaction + abonelik temizliği.
 * - `awaitHydration(key)` — hidrasyon bitişini bekleme (referans-parite;
 *   AppStorage senkron olduğundan pratikte anında çözülür).
 *
 * Persist formatı her zaman düz `{prop: value, ...}` JSON'dur (kaynak-referans
 * mobx-persist-store çıktısıyla aynı şekil). Eski zustand `{"state":...}`
 * sarmalı formatın okunması SÖKÜLDÜ — mevcut veriler app-boot'ta tek seferlik
 * migrasyonla düz formata çevrilir (bkz. platform/state/PersistedStorageMigration.ts).
 *
 * Kullanım (store constructor'ında):
 *   makeAutoObservable(this);
 *   makePersistent(this, "NotificationsStore", ["muted", "volume"], { syncAcrossTabs: true });
 */

import AppStorage from "@app/features/platform/state/PersistentStorage";
import { logger as rootLogger } from "@app/features/platform/utils/Logger";
import { isObservableMap, reaction, runInAction, toJS } from "mobx";

const logger = rootLogger.child("MobXPersistence");

/** Debounce'lu yazma gecikmesi (referans-parite: PERSIST_WRITE_DELAY_MS). */
const PERSIST_WRITE_DELAY_MS = 500;

interface PersistedStateEntry {
  /** mobx reaction disposer'ı. */
  stopReaction: () => void;
  /** syncAcrossTabs aboneliği (varsa). */
  stopSync?: () => void;
  /** Bekleyen debounce'lu yazmayı hemen uygular (varsa). */
  flush?: () => void;
  /** stopPersistent'ta doğru store'la çağrıldığını doğrulamak için. */
  store: object;
}

const persistedStates = new Map<string, PersistedStateEntry>();
const hydrationPromises = new Map<string, Promise<void>>();
const pendingPersistFlushes = new Set<() => void>();

/** Sayfa kapanırken bekleyen tüm debounce'lu yazmaları hemen uygular. */
function flushPendingPersistWrites(): void {
  for (const flush of [...pendingPersistFlushes]) {
    flush();
  }
}

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("pagehide", flushPendingPersistWrites);
  window.addEventListener("beforeunload", flushPendingPersistWrites);
}

/**
 * Ham JSON string'ini persist edilmiş düz nesneye çözer (`{"prop": value, ...}`).
 * (Eski zustand sarmalı format desteği kaldırıldı — boot migrasyonu düz
 * formata çevirir, bkz. PersistedStorageMigration.ts.)
 */
function parsePersistedValue(raw: string | null): Record<string, unknown> | null {
  if (raw === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    logger.warn("Persist edilmiş değer JSON olarak ayrıştırılamadı, atlanıyor:", error);
    return null;
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }
  return parsed as Record<string, unknown>;
}

/**
 * MOBX ERROR-38 KÖK-NEDEN FİX (2026-08-11, polling/console-hata dalgası):
 * `toJS(observableMap)` düz bir **Map** döndürür ve `JSON.stringify(Map)` her
 * zaman `{}` üretir → Map tutan store'lar (SelectedChannel.selectedChannelIds)
 * HİÇ persist edilemiyordu. Daha kötüsü: bir tüketici Map'i `Object.entries`
 * ile "objeye çevirmeye" kalktığında (ObservableMap `instanceof Map` DEĞİLDİR!)
 * mobx'un dahili alanları (`keysAtom_`/`data_`/...) sızıyor, sonraki
 * `toJS(Atom)` çağrısı `apiOwnKeys` → minified error 38 ile persistence
 * reaction'ını patlatıyordu (konsoldaki "[mobx] uncaught error in 'Reaction'"
 * MobXPersistence.ts:93). Çözüm bu katmanda: Map değerler JSON'a düz obje
 * olarak yazılır, hidrasyonda alanın MEVCUT tipi Map ise düz obje Map'e geri
 * çevrilir. (kaynak-referans mobx-persist-store aynı işi kendi serializer'ında yapar.)
 */
function toPlainForJson(value: unknown): unknown {
  const plain = toJS(value);
  if (plain instanceof Map) {
    const obj: Record<string, unknown> = {};
    for (const [k, v] of plain.entries()) obj[String(k)] = v;
    return obj;
  }
  if (plain instanceof Set) {
    return Array.from(plain.values());
  }
  return plain;
}

/** Alanın mevcut değeri Map-benzeri mi (ObservableMap `instanceof Map` değildir). */
function isMapLike(value: unknown): boolean {
  return value instanceof Map || isObservableMap(value);
}

/**
 * Store'un persist edilen alt kümesini deterministik anahtar sırasıyla
 * (properties dizisi sırası) JSON'a çevirir. Aynı kod her sekmede aynı
 * string'i üreteceğinden, cross-tab rehidrasyonu sonrası gereksiz geri-yazım
 * AppStorage'ın aynı-değer kontrolüne takılıp sessizce atlanır.
 */
function serializeSnapshot<T extends object>(store: T, properties: Array<keyof T>): string {
  const snapshot: Record<string, unknown> = {};
  for (const property of properties) {
    snapshot[property as string] = toPlainForJson(store[property]);
  }
  return JSON.stringify(snapshot);
}

/** Çözülmüş veriyi store'a (yalnızca listelenen property'lere) uygular. */
function applyToStore<T extends object>(store: T, properties: Array<keyof T>, data: Record<string, unknown>): void {
  runInAction(() => {
    for (const property of properties) {
      if (Object.prototype.hasOwnProperty.call(data, property as string)) {
        const incoming = data[property as string];
        const current = (store as Record<string, unknown>)[property as string];
        // Alan Map ise düz-obje persist formatını Map'e geri çevir (yukarıdaki
        // error-38 notu): alan tipi korunur, `.get/.set` çağrıları patlamaz.
        if (isMapLike(current) && incoming !== null && typeof incoming === "object" && !Array.isArray(incoming)) {
          (store as Record<string, unknown>)[property as string] = new Map(
            Object.entries(incoming as Record<string, unknown>),
          );
          continue;
        }
        (store as Record<string, unknown>)[property as string] = incoming;
      }
    }
  });
}

/**
 * Verilen storage anahtarı için hidrasyonun tamamlanmasını bekler.
 * Anahtar hiç persist edilmediyse hemen çözülür.
 */
export function awaitHydration(storageKey: string): Promise<void> {
  return hydrationPromises.get(storageKey) ?? Promise.resolve();
}

/**
 * Bir mobx store'unu AppStorage'a bağlar: önce hidrasyon, sonra listelenen
 * property'ler her değiştiğinde debounce'lu otomatik yazma.
 *
 * @param store       makeAutoObservable/makeObservable ile gözlemlenebilir store.
 * @param storageKey  AppStorage anahtarı (kaynak-referansta karşılığı olan store'lar
 *                    kaynak-referansın anahtar adını birebir kullanır).
 * @param properties  Persist edilecek property adları.
 * @param options.syncAcrossTabs  true ise diğer sekmedeki yazımlar bu store'a
 *                    otomatik yansıtılır (window `storage` olayı üzerinden).
 * @param options.writeDelayMs    Debounce gecikmesi (varsayılan 500ms; 0 = anında).
 * @param options.version         kaynak-referans imza paritesi için kabul edilir; düz
 *                    format sürüm alanı yazmadığından şu an işlevsizdir.
 */
export async function makePersistent<T extends object>(
  store: T,
  storageKey: string,
  properties: Array<keyof T>,
  options?: {
    syncAcrossTabs?: boolean;
    writeDelayMs?: number;
    version?: number;
  }
): Promise<void> {
  try {
    if (persistedStates.has(storageKey)) {
      logger.debug(`"${storageKey}" store'u zaten persist ediliyor, atlanıyor...`);
      return;
    }

    // --- Hidrasyon (senkron; Promise yalnızca kaynak-referans API paritesi için). ---
    const hydrate = (raw: string | null): void => {
      const data = parsePersistedValue(raw);
      if (data !== null) {
        applyToStore(store, properties, data);
      }
    };
    hydrate(AppStorage.getItem(storageKey));
    const hydrationPromise = Promise.resolve();
    hydrationPromises.set(storageKey, hydrationPromise);

    // --- Debounce'lu persist reaction'ı. ---
    const writeDelayMs = options?.writeDelayMs ?? PERSIST_WRITE_DELAY_MS;
    let pendingJson: string | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const flush = (): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      pendingPersistFlushes.delete(flush);
      if (pendingJson === null) {
        return;
      }
      const json = pendingJson;
      pendingJson = null;
      try {
        AppStorage.setItem(storageKey, json);
      } catch (error) {
        logger.error(`"${storageKey}" store'u AppStorage'a yazılamadı:`, error);
      }
    };

    const scheduleWrite = (json: string): void => {
      pendingJson = json;
      if (writeDelayMs <= 0) {
        flush();
        return;
      }
      if (timer !== null) {
        clearTimeout(timer);
      }
      pendingPersistFlushes.add(flush);
      timer = setTimeout(flush, writeDelayMs);
    };

    const stopReaction = reaction(
      () => serializeSnapshot(store, properties),
      (json) => {
        scheduleWrite(json);
      }
    );

    // --- Sekmeler-arası senkron (opsiyonel). ---
    let stopSync: (() => void) | undefined;
    if (options?.syncAcrossTabs) {
      stopSync = AppStorage.subscribe(
        (event) => {
          logger.debug(`"${storageKey}" store'u harici depolama değişikliği sonrası yeniden hidre ediliyor.`);
          // Bizim bekleyen (artık bayat) yazımımız diğer sekmenin verisini ezmesin.
          pendingJson = null;
          hydrate(event.newValue ?? AppStorage.getItem(storageKey));
        },
        {
          key: storageKey,
          source: "external",
        }
      );
    }

    persistedStates.set(storageKey, { stopReaction, stopSync, flush, store });
    logger.debug(`"${storageKey}" store'u AppStorage'dan hidre edildi ve artık persist ediliyor.`);
    await hydrationPromise;
  } catch (error) {
    logger.error(`"${storageKey}" store'u hidre edilemedi:`, error);
  }
}

/**
 * Persist etmeyi durdurur: bekleyen yazma hemen uygulanır, reaction ve
 * cross-tab aboneliği sonlandırılır. (kaynak-referans `stopPersistent` paritesi.)
 */
export function stopPersistent(storageKey: string, store: object): void {
  try {
    const persistedState = persistedStates.get(storageKey);
    if (persistedState === undefined) {
      return;
    }
    if (persistedState.store !== store) {
      logger.warn(`stopPersistent("${storageKey}") farklı bir store örneğiyle çağrıldı, yine de durduruluyor.`);
    }
    persistedState.flush?.();
    persistedState.stopSync?.();
    persistedState.stopReaction();
    persistedStates.delete(storageKey);
    hydrationPromises.delete(storageKey);
    logger.debug(`"${storageKey}" store'unun persist edilmesi durduruldu.`);
  } catch (error) {
    logger.error(`"${storageKey}" store'unun persist edilmesi durdurulamadı:`, error);
  }
}
